import { TextInput } from "@dropbox/dig-components/text_fields";
import { KeyCodes } from "@mirage/shared/util/constants";
import i18n from "@mirage/translations";
import { memo, useCallback, useState } from "react";
import styles from "./AnswerFollowUpInput.module.css";

import { PAPEvent } from "@mirage/analytics/events/base/event";
import { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import { PAP_Send_ComposePrompt } from "@mirage/analytics/events/types/send_compose_prompt";
import useSearchQueryId from "@mirage/shared/hooks/useSearchQueryId";
import type { KeyboardEvent } from "react";

interface AnswerFollowUpInputProps {
  onSubmit: (query: string) => void;
  waitingForResponse: boolean;
  logComposeEvent: (
    event: PAPEvent,
    overrides?: { actionSurfaceComponent?: ActionSurfaceComponent },
  ) => void;
  conversationId?: string;
  assistRequestId?: string;
  searchSessionId?: string;
}
/**
 * Presentational logic for the follow up input box in the MultiAnswerCard
 */
export const AnswerFollowUpInput = memo(
  ({
    onSubmit,
    waitingForResponse,
    logComposeEvent,
    conversationId,
    assistRequestId,
    searchSessionId,
  }: AnswerFollowUpInputProps) => {
    const [query, setQuery] = useState("");
    const { searchQueryUuid } = useSearchQueryId();
    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === KeyCodes.enter && query) {
          onSubmit(query);
          logComposeEvent(
            PAP_Send_ComposePrompt({
              queryString: query,
              queryLength: query.length,
              featureLine: "assist",
              actionSurfaceComponent: "compose_chat_pane",
              conversationId,
              assistRequestId,
              searchQueryUuid,
              searchSessionId,
            }),
          );
        }
      },
      [onSubmit, query, logComposeEvent, conversationId, searchQueryUuid],
    );
    return (
      <TextInput.Container
        className={styles.followUpInputContainer}
        size="large"
        isTransparent={false}
      >
        <TextInput.Input
          placeholder={i18n.t("answers_follow_up_placeholder")}
          onChange={(e) => setQuery(e.target.value)}
          value={query}
          onKeyDown={handleKeyDown}
          disabled={waitingForResponse}
        />
      </TextInput.Container>
    );
  },
);
AnswerFollowUpInput.displayName = "AnswerFollowUpInput";
